import { Box, LinearProgress, Link, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SecurityIcon from '@mui/icons-material/Security';
import ListIcon from '@mui/icons-material/List';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import InfoCard from './shared/InfoCard';

const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  color: '#718096',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  mb: 0.5,
  display: 'block',
};

const valueSx = {
  fontSize: 14,
  fontWeight: 600,
  color: '#2D3748',
};

const linkColor = '#3182CE';
const greenColor = '#16A34A';
const iconBlue = '#4299E1';

export interface PackageInformation {
  id: string;
  loginName: string;
  loginNameIsStarred?: boolean;
  package: string;
  quotaUsedGB: number;
  quotaTotalGB: number;
  clientHostLimit: string;
  server: string;
}

interface PackageInformationCardProps {
  data?: PackageInformation;
}

const PackageInformationCard = ({ data }: PackageInformationCardProps) => {
  const packageData: PackageInformation = data || {
    id: '13379',
    loginName: 'beumer-prive',
    loginNameIsStarred: true,
    package: 'VM 400GB',
    quotaUsedGB: 342.04,
    quotaTotalGB: 400.0,
    clientHostLimit: '8 / 8',
    server: 'box.01.com',
  };

  const percentage =
    packageData.quotaTotalGB > 0
      ? Math.round((packageData.quotaUsedGB / packageData.quotaTotalGB) * 100)
      : 0;

  return (
    <InfoCard
      title="Package Information"
      icon={<FolderIcon sx={{ fontSize: 18, color: iconBlue }} />}
    >
      {/* Row 1: ID, LOGIN NAME, PACKAGE */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="caption" sx={labelSx}>
            ID
          </Typography>
          <Typography variant="body2" sx={valueSx}>
            {packageData.id}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" sx={labelSx}>
            LOGIN NAME
          </Typography>
          <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
            <Link
              href="#"
              underline="hover"
              sx={{ fontSize: 14, fontWeight: 600, color: linkColor }}
            >
              {packageData.loginName}
            </Link>
            {packageData.loginNameIsStarred !== false && (
              <StarBorderIcon sx={{ fontSize: 16, color: '#718096' }} />
            )}
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" sx={labelSx}>
            PACKAGE
          </Typography>
          <Box display="flex" alignItems="center" sx={{ gap: 0.75 }}>
            <SecurityIcon sx={{ fontSize: 18, color: greenColor }} />
            <Typography variant="body2" sx={valueSx}>
              {packageData.package}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Row 2: QUOTA USAGE - label, usage text, progress bar, "% utilized" below bar */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={labelSx}>
          QUOTA USAGE
        </Typography>
        <Typography variant="body2" sx={{ ...valueSx, fontSize: 13, mb: 0.75 }}>
          {packageData.quotaUsedGB.toFixed(2)} GB / {packageData.quotaTotalGB.toFixed(2)} GB
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: '#E2E8F0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: iconBlue,
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{ fontSize: 12, color: '#718096', mt: 0.5, textAlign: 'right' }}
        >
          {percentage}% utilized
        </Typography>
      </Box>

      {/* Row 3: MODULES, CLIENT HOST LIMIT, SERVER */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        <Box>
          <Typography variant="caption" sx={labelSx}>
            MODULES
          </Typography>
          <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
            <ListIcon sx={{ fontSize: 20, color: iconBlue }} />
            <ViewModuleIcon sx={{ fontSize: 20, color: iconBlue }} />
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" sx={labelSx}>
            CLIENT HOST LIMIT
          </Typography>
          <Typography variant="body2" sx={{ ...valueSx, color: greenColor }}>
            {packageData.clientHostLimit}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" sx={labelSx}>
            SERVER
          </Typography>
          <Link
            href="#"
            underline="hover"
            sx={{ fontSize: 14, fontWeight: 600, color: linkColor }}
          >
            {packageData.server}
          </Link>
        </Box>
      </Box>
    </InfoCard>
  );
};

export default PackageInformationCard;
